import { Box, Inline, Stack, xcss, Flex } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import React from 'react';

import { DEFAULT_SKELETON_HEIGHT, CARD_WIDTH } from './constants';

const SKELETON_CONFIG = {
  isShimmering: true
} as const;

const headerStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  paddingInline: 'space.050',
  paddingBlock: 'space.050',
  paddingBottom: 'space.150',
  borderRadius: 'radius.large'
});

const titleBarStyles = xcss({
  height: '24px'
});

const columnContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  marginTop: 'space.200',
  marginLeft: 'space.600',
  backgroundColor: 'color.skeleton.subtle',
  padding: 'space.100',
  borderRadius: 'radius.medium',
  width: 'fit-content'
});

const skeletonCardContainerStyles = xcss({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 'radius.medium',
  boxShadow: 'elevation.shadow.raised',
  overflow: 'hidden',
  width: `${CARD_WIDTH}px`,
  backgroundColor: 'elevation.surface'
});

const skeletonCardShadowStyles = xcss({
  position: 'absolute',
  insetBlockStart: '0',
  insetInlineEnd: '0',
  insetBlockEnd: '0',
  insetInlineStart: '0',
  borderRadius: 'radius.050',
  boxShadow: 'elevation.shadow.raised',
  pointerEvents: 'none'
});

const cardBodyStyles = xcss({
  position: 'relative',
  zIndex: '1',
  overflow: 'hidden',
  minHeight: '32px',
  overflowWrap: 'anywhere',
  paddingInline: 'space.150',
  paddingBlockStart: 'space.150',
  paddingBlockEnd: 'space.150',
  gap: 'space.100'
});

const titleContainerStyles = xcss({
  marginBottom: 'space.250'
});

const fieldContainerStyles = xcss({
  gap: 'space.100',
  marginBottom: 'space.250'
});

const footerContainerStyles = xcss({
  marginTop: 'space.300'
});

const addButtonContainerStyles = xcss({
  marginBlockStart: 'space.150',
  marginBlockEnd: 'space.050',
  paddingInline: 'space.100',
  paddingBlock: 'space.075',
  borderRadius: 'radius.small',
  backgroundColor: 'color.background.neutral.subtle'
});

const SkeletonItem = ({
  width,
  height,
  borderRadius = 3
}: {
  width: string;
  height: string;
  borderRadius?: number;
}) => (
  <Skeleton
    width={width}
    height={height}
    borderRadius={borderRadius}
    {...SKELETON_CONFIG}
  />
);

const FieldSkeleton = ({
  width = '78px',
  showInline = false,
  showFieldHeader = false
}: { width?: string; showInline?: boolean; showFieldHeader?: boolean } = {}) => (
  <Stack xcss={fieldContainerStyles}>
    {showFieldHeader && <SkeletonItem width={width} height={DEFAULT_SKELETON_HEIGHT} />}
    {showInline ? (
      <Inline space="space.100">
        <SkeletonItem width="46px" height={DEFAULT_SKELETON_HEIGHT} />
        <SkeletonItem width="46px" height={DEFAULT_SKELETON_HEIGHT} />
        <SkeletonItem width="46px" height={DEFAULT_SKELETON_HEIGHT} />
      </Inline>
    ) : (
      <SkeletonItem width="100%" height={DEFAULT_SKELETON_HEIGHT} />
    )}
  </Stack>
);

export const DatabaseBoardSkeleton = () => {
  return (
    <Box xcss={columnContainerStyles} testId="database-board-skeleton">
      {/* Column Header Container */}
      <Box xcss={headerStyles}>
        <Inline space="space.100" alignBlock="center" xcss={titleBarStyles}>
          <SkeletonItem width="14px" height={DEFAULT_SKELETON_HEIGHT} />
          <SkeletonItem width="78px" height={DEFAULT_SKELETON_HEIGHT} />
          <SkeletonItem width="14px" height={DEFAULT_SKELETON_HEIGHT} />
        </Inline>
      </Box>

      {/* Card Container */}
      <Box xcss={skeletonCardContainerStyles}>
        <Box xcss={skeletonCardShadowStyles} />
        <Stack xcss={cardBodyStyles}>
          {/* Title */}
          <Box xcss={titleContainerStyles}>
            <SkeletonItem width="100%" height={DEFAULT_SKELETON_HEIGHT} />
          </Box>

          {/* Fields */}
          <Stack>
            <FieldSkeleton width="78px" showFieldHeader />
            <FieldSkeleton width="100%" />
            <FieldSkeleton width="78px" showInline showFieldHeader />
          </Stack>

          {/* Footer */}
          <Flex
            xcss={footerContainerStyles}
            alignItems="center"
            justifyContent="space-between"
          >
            <SkeletonItem width="86px" height="10px" />
            <SkeletonItem width="12px" height="12px" />
          </Flex>
        </Stack>
      </Box>

      {/* Add Entry Button */}
      <Box xcss={addButtonContainerStyles}>
        <SkeletonItem width="78px" height="10px" />
      </Box>
    </Box>
  );
};
